(function () {
  const marker = '__acquaNativeCartRedirectBound';
  const refreshMarker = '__acquaNativeCartRefreshBound';
  let mutationGeneration = 0;
  let settledGeneration = 0;
  let settledStatus = '';
  let staleRefreshPending = false;
  let refreshChain = Promise.resolve();
  let refreshCount = 0;

  function isCartPage() {
    if (document.body && document.body.classList.contains('template-cart')) return true;

    const cartUrl = window.Theme && window.Theme.routes && window.Theme.routes.cart_url
      ? window.Theme.routes.cart_url
      : '/cart';
    try {
      const current = new URL(window.location.href).pathname.replace(/\/$/, '');
      const cart = new URL(cartUrl, window.location.href).pathname.replace(/\/$/, '');
      return current === cart;
    } catch (_error) {
      return false;
    }
  }

  function cartUrl() {
    return window.Theme && window.Theme.routes && window.Theme.routes.cart_url
      ? window.Theme.routes.cart_url
      : '/cart';
  }

  function sectionContext(root) {
    const data = root.querySelector('[data-section-type="static-cart"][data-section-id]');
    const container = data && data.closest && data.closest('.shopify-section');
    const id = data && data.getAttribute('data-section-id');
    return data && container && id ? { container, id } : null;
  }

  function focusIdentity() {
    const active = document.activeElement;
    const control = active && active.closest && active.closest('acqua-linked-selection');
    if (!control) return null;
    return {
      group: control.getAttribute('group'),
      key: control.getAttribute('line-key'),
      action: active.getAttribute && active.getAttribute('data-acqua-quantity-action'),
      input: active.tagName && active.tagName.toLowerCase() === 'input',
    };
  }

  function restoreFocus(identity) {
    if (!identity) return;
    const controls = document.querySelectorAll('acqua-linked-selection');
    const control = Array.from(controls).find(function (candidate) {
      return identity.group
        ? candidate.getAttribute('group') === identity.group
        : candidate.getAttribute('line-key') === identity.key;
    });
    const target = identity.input ? control && control.querySelector('input')
      : control && control.querySelector(`[data-acqua-quantity-action="${identity.action || ''}"]`);
    if (target && !target.disabled && target.focus) target.focus({ preventScroll: true });
  }

  function setRefreshPending(active, identity) {
    refreshCount += active ? 1 : -1;
    if (refreshCount < 0) refreshCount = 0;
    document.documentElement.toggleAttribute('data-acqua-native-cart-refreshing', refreshCount > 0);
    document.dispatchEvent(new CustomEvent('acqua:cart-external-pending', { detail: {
      active: refreshCount > 0,
      group: identity && identity.group,
      keys: identity && identity.key ? [identity.key] : [],
      message: 'Updating your cart…',
    } }));
  }

  function replaceTarget(currentRoot, nextRoot, selector) {
    const current = currentRoot.querySelector(selector);
    const next = nextRoot.querySelector(selector);
    if (!current || !next) return false;
    current.replaceWith(next.cloneNode(true));
    return true;
  }

  function applySection(html, identity) {
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const current = sectionContext(document);
    const next = sectionContext(parsed);
    if (!current || !next || current.id !== next.id) return false;
    const currentRoot = current.container;
    const nextRoot = next.container;
    if (!nextRoot.querySelector('[data-cart-item-list]')) {
      const nextForm = nextRoot.querySelector('form');
      const currentForm = currentRoot.querySelector('form');
      if (!nextForm || !currentForm) return false;
      currentForm.replaceWith(nextForm.cloneNode(true));
      return true;
    }
    if (!replaceTarget(currentRoot, nextRoot, '[data-cart-item-list]')) return false;
    if (!replaceTarget(currentRoot, nextRoot, '[data-cart-discounts]')) return false;

    const currentTotals = currentRoot.querySelectorAll('[data-cart-total]');
    const nextTotals = nextRoot.querySelectorAll('[data-cart-total]');
    if (!currentTotals.length || currentTotals.length !== nextTotals.length) return false;
    currentTotals.forEach(function (total, index) { total.innerHTML = nextTotals[index].innerHTML; });

    const currentShipping = currentRoot.querySelectorAll('[data-free-shipping-bar]');
    const nextShipping = nextRoot.querySelectorAll('[data-free-shipping-bar]');
    if (currentShipping.length !== nextShipping.length) return false;
    currentShipping.forEach(function (bar, index) { bar.replaceWith(nextShipping[index].cloneNode(true)); });
    restoreFocus(identity);
    document.dispatchEvent(new CustomEvent('acqua:cart-presentation-refresh'));
    return true;
  }

  function fallbackReload(generation) {
    if (generation === mutationGeneration) window.location.reload();
  }

  function deferStaleRefresh() {
    staleRefreshPending = true;
    if (settledGeneration === mutationGeneration && settledStatus !== 'UPDATED') {
      staleRefreshPending = false;
      fallbackReload(mutationGeneration);
    }
  }

  function scheduleSectionRefresh(result, generation, identity) {
    if (!result || result.status !== 'UPDATED') return;
    if (window.acquaCartDrawer && window.acquaCartDrawer.active()) {
      void window.acquaCartDrawer.refreshCart();
      if (!isCartPage()) return;
    }
    if (!result.cart || !Array.isArray(result.cart.items) || result.cart.items.length === 0) {
      fallbackReload(generation);
      return;
    }
    setRefreshPending(true, identity);
    refreshChain = refreshChain.then(async function () {
      if (generation !== mutationGeneration) return deferStaleRefresh();
      const section = sectionContext(document);
      if (!section) return fallbackReload(generation);
      try {
        const url = new URL(cartUrl(), window.location.href);
        url.searchParams.set('section_id', section.id);
        const response = await window.fetch(url.href, {
          cache: 'no-store',
          headers: { Accept: 'text/html' },
          signal: AbortSignal.timeout(15000),
        });
        if (generation !== mutationGeneration) {
          deferStaleRefresh();
          return;
        }
        if (!response.ok) return fallbackReload(generation);
        const html = await response.text();
        if (generation !== mutationGeneration) return deferStaleRefresh();
        if (!applySection(html, identity)) fallbackReload(generation);
      } catch (_error) {
        fallbackReload(generation);
      }
    }).finally(function () { setRefreshPending(false, identity); });
  }

  function bindSectionRefresh() {
    const api = window.acquaLinkedCart;
    if (!api || (!isCartPage() && !window.acquaCartDrawer)) return;
    ['update', 'updateLine', 'removeOptional'].forEach(function (name) {
      const original = api[name];
      if (typeof original !== 'function' || original[refreshMarker] === true) return;
      function withSectionRefresh() {
        mutationGeneration += 1;
        const generation = mutationGeneration;
        const identity = focusIdentity();
        if (window.acquaCartDrawer) window.acquaCartDrawer.beforeMutation();
        const operation = original.apply(this, arguments);
        if (operation && typeof operation.then === 'function') {
          void operation.then(function (result) {
            settledGeneration = generation;
            if (window.acquaCartDrawer && !window.acquaCartDrawer.active()) window.acquaCartDrawer.settled();
            settledStatus = result && result.status || '';
            if (result && result.status === 'UPDATED') {
              staleRefreshPending = false;
              if (isCartPage() || (window.acquaCartDrawer && window.acquaCartDrawer.active())) scheduleSectionRefresh(result, generation, identity);
            } else if (window.acquaCartDrawer && window.acquaCartDrawer.active()) {
              window.acquaCartDrawer.settled();
            } else if (staleRefreshPending) {
              staleRefreshPending = false;
              fallbackReload(generation);
            }
          }, function () {
            settledGeneration = generation;
            settledStatus = 'REJECTED';
            if (window.acquaCartDrawer) window.acquaCartDrawer.settled();
            if (staleRefreshPending) {
              staleRefreshPending = false;
              fallbackReload(generation);
            }
            // The original promise remains responsible for reporting the failure.
          });
        }
        return operation;
      }
      Object.defineProperty(withSectionRefresh, refreshMarker, { value: true });
      api[name] = withSectionRefresh;
    });
  }

  function blockCheckoutDuringRefresh(event) {
    if (!refreshCount) return;
    const target = event.target;
    const linkedControl = target && target.closest && target.closest('acqua-linked-selection');
    const checkout = event.type === 'submit' || target && target.closest && target.closest([
      '[name="checkout"]',
      '[data-acqua-checkout]',
      '.additional-checkout-buttons button',
      '.additional-checkout-buttons a',
      'a[href="/checkout"]',
      'a[href^="/checkout?"]',
      'a[href^="/checkout#"]',
    ].join(', '));
    if (!linkedControl && !checkout) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  }

  function bindNativeCartRedirect() {
    const api = window.acquaLinkedCart;
    if (!api || typeof api.add !== 'function' || api.add[marker] === true) return;

    const originalAdd = api.add;
    function addWithNativeRedirect() {
      if (window.acquaCartDrawer) window.acquaCartDrawer.beforeMutation({ adding: true });
      const operation = originalAdd.apply(this, arguments);
      if (operation && typeof operation.then === 'function') {
        void operation.then(function (result) {
          if (result && result.status === 'ADDED') {
            if (window.acquaCartDrawer) void window.acquaCartDrawer.openVerified();
            else if (!isCartPage()) window.location.assign(cartUrl());
          } else if (window.acquaCartDrawer) window.acquaCartDrawer.settled({ failed: true });
        }, function () {
          if (window.acquaCartDrawer) window.acquaCartDrawer.settled();
          // The original promise remains responsible for reporting the failure.
        });
      }
      return operation;
    }

    Object.defineProperty(addWithNativeRedirect, marker, { value: true });
    api.add = addWithNativeRedirect;
  }

  function bind() {
    bindNativeCartRedirect();
    bindSectionRefresh();
  }

  document.addEventListener('click', blockCheckoutDuringRefresh, true);
  document.addEventListener('change', blockCheckoutDuringRefresh, true);
  document.addEventListener('submit', blockCheckoutDuringRefresh, true);
  document.addEventListener('acqua:cart-state', bind);
  document.addEventListener('acqua:cart-presentation-refresh', bind);
  bind();
})();
